import { Plus, X } from 'lucide-react-native';
import { type ComponentRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { useStore } from 'zustand';

import { StatementSummary } from './StatementSummary';
import type { ExtractedStatement } from '../../model/extractedStatements';
import { isValidReferenceUrl } from '../../model/referenceUrl';
import {
  getProvideReferenceDraftStore,
  selectValueDraft,
} from '../../state/provideReferenceDraftStore';

import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { ScrollView } from '@/components/ui/scroll-view';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

interface ProvideReferenceListProps {
  messageSid: string;
  /** Full extracted-statements array; rows index into it. */
  extractedStatements: readonly ExtractedStatement[];
  /** Statement indices to render rows for — all extracted statements,
   * in order. */
  statementIndices: readonly number[];
  /** True while the publish write is in flight; disables every control. */
  isPublishing: boolean;
  /** Publish trigger fired from a row's keyboard submit (Enter / "done").
   * Lets a keyboard-first expert submit without reaching for the mouse;
   * the handler self-gates on `canPublish`, so Enter on an empty/in-flight
   * form is a no-op. Optional — callers that don't wire it simply lose the
   * Enter-to-publish affordance. */
  onSubmit?: () => void;
}

/**
 * Provide-phase list rendered inside the citation wizard when the
 * expert entered via "I have references in mind". One row per statement,
 * but every row is **opt-in**: it shows a quiet "Add a reference"
 * affordance until tapped, then reveals a single free-text field (paste
 * a URL to attach a verbatim reference, or describe what you're looking
 * for and Primum will find it). The expert fills only the statements
 * they have — empty rows are skipped at publish — so there's no
 * implication that every statement must be filled.
 */
export function ProvideReferenceList({
  messageSid,
  extractedStatements,
  statementIndices,
  isPublishing,
  onSubmit,
}: ProvideReferenceListProps) {
  const store = getProvideReferenceDraftStore(messageSid);

  // The first renderable row opens with its field expanded and focused so
  // the expert lands ready to type instead of having to tap "Add a
  // reference" first. Tracked as the index of the first statement that
  // actually resolves to a row (skipping any gaps), so the auto-focus lands
  // on the first *visible* row even if statement 0 is missing.
  let firstRenderedIndex: number | null = null;

  return (
    <ScrollView className="max-h-[60vh] w-full" keyboardShouldPersistTaps="handled">
      <VStack className="w-full px-4 pb-4 pt-2" space="sm">
        <Text size="sm" className="px-1 text-typography-500">
          Add references for the statements you have — you don't need to do every one.
        </Text>
        {statementIndices.map((statementIndex) => {
          const statement = extractedStatements[statementIndex];

          if (!statement) {
            return null;
          }

          const isFirstRow = firstRenderedIndex === null;

          if (isFirstRow) {
            firstRenderedIndex = statementIndex;
          }

          return (
            <ProvideReferenceRow
              key={`provide-row-${statementIndex}`}
              store={store}
              statement={statement}
              statementIndex={statementIndex}
              isPublishing={isPublishing}
              autoFocus={isFirstRow}
              onSubmit={onSubmit}
            />
          );
        })}
      </VStack>
    </ScrollView>
  );
}

/**
 * The live route hint for a provide row's value: how it will be
 * published. `null` (no hint) for an empty field.
 */
function pickRouteHint(value: string): string | null {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return null;
  }

  return isValidReferenceUrl(trimmed)
    ? 'Attaches as a reference'
    : 'Primum will find this reference';
}

interface ProvideReferenceRowProps {
  store: ReturnType<typeof getProvideReferenceDraftStore>;
  statement: ExtractedStatement;
  statementIndex: number;
  isPublishing: boolean;
  /** When true, the row mounts expanded with its field focused (used for
   * the first row so the expert can type immediately). */
  autoFocus?: boolean;
  /** Fired on the field's keyboard submit (Enter / "done"). */
  onSubmit?: () => void;
}

/**
 * Single opt-in provide row: the statement summary plus either a quiet
 * "Add a reference" button (collapsed) or a single free-text field with
 * a clear control (expanded). A row expands when the expert taps Add or
 * when it already carries a draft value; clearing collapses it back.
 * Subscribed to the per-message draft store so typing in one row doesn't
 * re-render the others.
 */
function ProvideReferenceRow({
  store,
  statement,
  statementIndex,
  isPublishing,
  autoFocus,
  onSubmit,
}: ProvideReferenceRowProps) {
  const value = useStore(store, (state) => selectValueDraft(state, statementIndex));

  const setValueDraft = useStore(store, (state) => state.setValueDraft);

  const [isOpen, setIsOpen] = useState(value.trim().length > 0 || Boolean(autoFocus));

  const inputRef = useRef<ComponentRef<typeof InputField>>(null);

  const hintId = useId();

  const hint = pickRouteHint(value);

  // Land focus in the first row's field on mount so a keyboard-first
  // expert can type immediately instead of tapping "Add a reference" first.
  // Runs once; later rows stay opt-in (no pressure to fill every one).
  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    requestAnimationFrame(() => {
      // @ts-expect-error Gluestack InputField ref drops the underlying TextInput.focus()
      inputRef.current?.focus();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = useCallback(() => {
    setIsOpen(true);

    // Focus the freshly-revealed field so the expert can type immediately.
    requestAnimationFrame(() => {
      // @ts-expect-error Gluestack InputField ref drops the underlying TextInput.focus()
      inputRef.current?.focus();
    });
  }, []);

  // Enter / "done" submits the whole provide form. `onSubmit` self-gates on
  // `canPublish`, so this is a no-op when nothing is fillable or a publish
  // is already in flight. Keyboard is dismissed first on native.
  const handleSubmitEditing = useCallback(() => {
    Keyboard.dismiss();

    onSubmit?.();
  }, [onSubmit]);

  // Collapse an untouched row back to its "Add a reference" link when focus
  // leaves it. Pairs with the focus-to-open affordance so tabbing through
  // the rows doesn't strand a trail of empty open fields — only rows the
  // expert actually typed into stay expanded. Reads the live draft (not the
  // render-time closure) so a value entered just before blur is respected.
  const handleBlur = useCallback(() => {
    if (selectValueDraft(store.getState(), statementIndex).trim().length === 0) {
      setIsOpen(false);
    }
  }, [store, statementIndex]);

  const handleRemove = useCallback(() => {
    setValueDraft(statementIndex, '');

    setIsOpen(false);
  }, [setValueDraft, statementIndex]);

  return (
    <VStack
      space="xs"
      className="overflow-hidden rounded-md border border-outline-100 bg-background-0 p-3">
      {/* StatementSummary applies `flex-1`, which is meant to grow
          horizontally beside a sibling (e.g. the selection-row checkbox).
          Wrapped in an HStack here so that growth stays on the horizontal
          axis; placed bare in this column it resolves to a vertical
          `flex-basis: 0` and Yoga collapses the text to zero height on the
          relayout a sibling row's expansion triggers — making the
          statement vanish on native. */}
      <HStack>
        <StatementSummary statement={statement} numberOfLines={2} />
      </HStack>
      {isOpen ? (
        <VStack space="xs">
          <Input size="md" isDisabled={isPublishing}>
            <InputField
              ref={inputRef}
              placeholder="Name the study, or paste a link"
              value={value}
              onChangeText={(next) => setValueDraft(statementIndex, next)}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="done"
              onSubmitEditing={handleSubmitEditing}
              onBlur={handleBlur}
              aria-label="Reference or description"
              aria-describedby={hint ? hintId : undefined}
              accessibilityHint={hint ?? undefined}
            />
            <InputSlot
              className="pr-3"
              hitSlop={ICON_BUTTON_HIT_SLOP}
              onPress={handleRemove}
              disabled={isPublishing}
              {...getAccessibilityProps({ label: 'Remove this reference', role: 'button' })}>
              <InputIcon as={X} className="text-typography-500" />
            </InputSlot>
          </Input>
          {hint ? (
            <Text size="xs" nativeID={hintId} className="px-1 text-typography-500">
              {hint}
            </Text>
          ) : null}
        </VStack>
      ) : (
        <Button
          variant="link"
          size="sm"
          onPress={handleAdd}
          onFocus={handleAdd}
          isDisabled={isPublishing}
          className="self-start"
          {...getAccessibilityProps({
            label: 'Add a reference for this statement',
            role: 'button',
          })}>
          <ButtonIcon as={Plus} size="sm" />
          <ButtonText>Add a reference</ButtonText>
        </Button>
      )}
    </VStack>
  );
}
